import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getTodosUsuarios } from '../services/apiUsuario';
import Spinner from '../components/spinner/spinner';
import 'react-toastify/dist/ReactToastify.css';
import { erroToastify } from '../shared/validacaoToastify';
import usePageReload from '../shared/usePageReload';
import { jwtDecode } from 'jwt-decode';
interface User {
  cdUsuario: string;
  nmUsuario: string;
  dsLogin: string;
  cdPerfil: string;
  dmBloqueado: string;
  dmSituacao: string
  tokenString: string;
}
interface AuthContextProps {
  user: User;
  isAuthenticated: boolean;
  isAdmin: boolean;
  estaAutorizado: boolean;
  login: (userData: User) => void;
  logout: () => void;
  loading: boolean;
  previousRoute: string | null; 
}
interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

//Hook Criado para pegar as rota de forma Global e pega sempre a rota anterior em relação a atual;
const usePreviousRoute = () => {
  const location = useLocation();
  const previousRoute = useRef<string | null>(null);
  const currentRoute = useRef<string | null>(null);

  useEffect(() => {
    if (currentRoute.current) {
      previousRoute.current = currentRoute.current;
    }
    currentRoute.current = location.pathname;
  }, [location.pathname]);

  return previousRoute.current;
};

export default usePreviousRoute;
///

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User>({
    'cdUsuario': '0',
    'nmUsuario': 'Anonimo',
    'dsLogin': 'Anonimo',
    'cdPerfil': '0',
    'dmBloqueado': '',
    'dmSituacao': '',
    'tokenString': ''
  });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAdmin, SetIsAdmin] = useState<boolean>(false)
  const [estaAutorizado, setEstaAutorizado] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [dadosAtualizados, setDadosAtualizados] = useState<User | null>(null);
  const location = useLocation();
  const isPageReload = usePageReload();
  const previousRoute = usePreviousRoute(); 

  useEffect(() => {
    const expiracaoDoToken = () => {
      const token = localStorage.getItem('tokenString');
      if (token) {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp !== undefined && decodedToken.exp < currentTime) {
          erroToastify('Sessão expirada. Por favor, faça login novamente.', {
            autoClose: false,
            position: 'top-right',
            theme: 'dark',
            closeButton: true,
          });
          logout();
          setDadosAtualizados(null);
        }
      }
    };

    let previousUrl = window.location.href;

    if (isAuthenticated) {
      const cdUsuario = localStorage.getItem('cdUsuario') || '0';

      const buscarUsuarioBloqueado = async () => {
        try {
          const response = await getTodosUsuarios(+cdUsuario);
          setDadosAtualizados(response.data);
        } catch (error) {
          console.error('Erro ao buscar dados do usuário:', error);
        }
      };

      if (location.pathname !== previousUrl) {
        buscarUsuarioBloqueado();
        previousUrl = window.location.pathname;
      }

      const dadosDeslogadoBlock = dadosAtualizados && dadosAtualizados.dmBloqueado;
      const dadosDeslogadoRemove = dadosAtualizados && !dadosAtualizados.dmSituacao;

      //Logout Forçado se o Usuário for bloqueado;
      if (dadosDeslogadoBlock || (isPageReload && dadosDeslogadoBlock)) {
        erroToastify('\nUsuário bloqueado 🚫 \n\n Contacte um Administrador.', {
          autoClose: 7000,
          position: "top-right",
          theme: "dark"
        })
        logout();
        setDadosAtualizados(null);
        //Logout Forçado se o Usuário for deletado(lógicamente);
      } else if (dadosDeslogadoRemove || (isPageReload && dadosDeslogadoRemove)) {
        erroToastify('\nUsuário Removido 🗑 \n\n Contacte um Administrador.', {
          autoClose: 7000,
          position: "top-right",
          theme: "dark"
        });
        logout();
        setDadosAtualizados(null);
      }
      //Logout Forçado se o token expirar;
      expiracaoDoToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, isAuthenticated, dadosAtualizados?.dmBloqueado, dadosAtualizados?.dmSituacao]);

  const loadUserFromLocalStorage = () => {
    if (localStorage.getItem('cdUsuario') != null) {
      const cdUsuario = localStorage.getItem('cdUsuario') || '0';
      const nmUsuario = localStorage.getItem('nmUsuario') || 'Anonimo';
      const dsLogin = localStorage.getItem('dsLogin') || 'Anonimo';
      const cdPerfil = localStorage.getItem('cdPerfil') || '0';
      const dmBloqueado = localStorage.getItem('dmBloqueado') || '';
      const dmSituacao = localStorage.getItem('dmSituacao') || '';
      const tokenString = localStorage.getItem('tokenString') || '';
      if (cdUsuario && nmUsuario) {
        const userData = { cdUsuario, nmUsuario, dsLogin, cdPerfil, dmBloqueado, dmSituacao, tokenString };
        setUser(userData);
        setIsAuthenticated(true);
        if (cdPerfil === '1') {
          SetIsAdmin(true);
        }
        if (cdPerfil !== '1' && dmBloqueado === 'true') {
          setEstaAutorizado(false)
          logout()
        }
      }
    } else {
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    loadUserFromLocalStorage();
    setLoading(false);
  }, []);

  const login = (userData: User) => {
    localStorage.setItem('cdUsuario', userData.cdUsuario);
    localStorage.setItem('nmUsuario', userData.nmUsuario);
    localStorage.setItem('dsLogin', userData.dsLogin);
    localStorage.setItem('cdPerfil', userData.cdPerfil);
    localStorage.setItem('dmBloqueado', userData.dmBloqueado.toString());
    localStorage.setItem('tokenString', userData.tokenString);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('cdUsuario');
    localStorage.removeItem('nmUsuario');
    localStorage.removeItem('dsLogin');
    localStorage.removeItem('cdPerfil');
    localStorage.removeItem('dmBloqueado');
    localStorage.removeItem('tokenString');
    setIsAuthenticated(false);
    navigate('/milshop');
   
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isAdmin, estaAutorizado, login, logout, loading, previousRoute}}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
